import React, { useState, useEffect } from 'react';
import { setPageMeta } from '../utils/setPageMeta';
import { ArrowLeft, Calendar, User, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ContactModal from '../components/ContactModal';

const BlogPost2 = () => {
  useEffect(() => {
    setPageMeta(
      `Estate Planning Essentials: Protecting Your Family's Future - Legal Flick Blog`,
      `Comprehensive guide to estate planning including wills, trusts, power of attorney, and healthcare directives. Protect your family and preserve your legacy.`
    );
  }, []);

  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      {/* Blog Post Content */}
      <article className="flex-1 py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Button */}
          <Button
            variant="ghost"
            className="mb-8"
            onClick={() => window.history.back()}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Blog
          </Button>

          {/* Article Header */}
          <header className="mb-12">
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              Estate Planning Essentials: Protecting Your Family's Future
            </h1>
            
            <div className="flex flex-wrap items-center gap-6 text-gray-600 mb-8">
              <div className="flex items-center">
                <User className="w-5 h-5 mr-2" />
                <span>Legal Team</span>
              </div>
              <div className="flex items-center">
                <Clock className="w-5 h-5 mr-2" />
                <span>10 min read</span>
              </div>
            </div>

            <img
              src="https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=1200&h=600&fit=crop"
              alt="Estate Planning Guide"
              className="w-full h-[400px] object-cover rounded-lg shadow-lg"
            />
          </header>

          {/* Article Content */}
          <div className="prose prose-lg max-w-none">
            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">What is Estate Planning?</h2>
            <p className="text-gray-700 mb-6 leading-relaxed">
              Estate planning is the process of arranging for the management and distribution of your assets after death or incapacitation. It's not just for the wealthy - everyone with assets, dependents, or specific wishes for end-of-life care needs an estate plan.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Essential Estate Planning Documents</h2>
            <p className="text-gray-700 mb-6 leading-relaxed">
              A comprehensive estate plan typically includes these key documents:
            </p>
            <ul className="list-disc pl-6 mb-8 space-y-3 text-gray-700">
              <li><strong>Last Will and Testament:</strong> Specifies how your assets will be distributed and names guardians for minor children</li>
              <li><strong>Living Trust:</strong> Allows assets to pass to beneficiaries without probate court involvement</li>
              <li><strong>Power of Attorney:</strong> Designates someone to make financial decisions if you become incapacitated</li>
              <li><strong>Healthcare Directive:</strong> Documents your wishes for medical care and end-of-life decisions</li>
              <li><strong>Beneficiary Designations:</strong> Keep insurance policies and retirement accounts up to date</li>
            </ul>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Why Estate Planning Matters</h2>
            <p className="text-gray-700 mb-6 leading-relaxed">
              Without proper estate planning, you leave critical decisions to state law and the courts:
            </p>
            <ul className="list-disc pl-6 mb-8 space-y-3 text-gray-700">
              <li>The state decides who inherits your assets based on intestacy laws</li>
              <li>Courts may appoint guardians for your children that you wouldn't have chosen</li>
              <li>Your family faces lengthy and expensive probate proceedings</li>
              <li>Family disputes can arise over asset distribution and care decisions</li>
              <li>More of your estate goes to taxes and legal fees instead of your loved ones</li>
            </ul>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">When to Review Your Estate Plan</h2>
            <p className="text-gray-700 mb-6 leading-relaxed">
              Your estate plan should be reviewed and updated regularly, especially after:
            </p>
            <ol className="list-decimal pl-6 mb-8 space-y-3 text-gray-700">
              <li>Marriage, divorce, or remarriage</li>
              <li>Birth or adoption of children or grandchildren</li>
              <li>Significant changes in assets or financial situation</li>
              <li>Death of a beneficiary or named executor</li>
              <li>Moving to a different state</li>
              <li>Changes in tax laws that affect estate planning</li>
            </ol>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6">Common Estate Planning Myths</h2>
            <p className="text-gray-700 mb-6 leading-relaxed">
              Don't let these misconceptions prevent you from protecting your family:
            </p>
            <ul className="list-disc pl-6 mb-8 space-y-3 text-gray-700">
              <li><strong>"I'm too young to need estate planning"</strong> - Accidents and illness can happen at any age</li>
              <li><strong>"I don't have enough assets"</strong> - Estate planning is about more than just wealth</li>
              <li><strong>"A will is enough"</strong> - Wills alone may not avoid probate or protect assets</li>
              <li><strong>"It's too expensive"</strong> - The cost of not planning is much higher</li>
            </ul>

            <div className="bg-blue-50 border-l-4 border-blue-600 p-6 my-12">
              <p className="text-gray-800 font-semibold mb-2">Protect your family's future today</p>
              <p className="text-gray-700 mb-4">Our estate planning experts can help you create a comprehensive plan tailored to your family's needs.</p>
              <Button
                className="bg-blue-600 hover:bg-blue-700 text-white"
                onClick={() => setIsModalOpen(true)}
              >
                Get Started Today
              </Button>
            </div>
          </div>
        </div>
      </article>

      <Footer />
      <ContactModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
};

export default BlogPost2;